import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ErrorHandler } from "UI/Errors";
import { InvalidOperationException } from "../../../../../Global/Base/Exceptions";
import { IDisposable } from "../../../../../Global/Base/IDisposable";
import { EnumUtils } from "../../../../../Global/Base/Utils/EnumUtils";
import { ValidationErrorType } from "../../../../../OW.Remoting/Basic/EOWExceptionsInterfaces";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { IEnableInEditControl } from "../../../../UI.BaseControls/ControlInterfaces";
import { ActionContext, ActionType, IActionContext, SelectionType } from "../../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../../UI.Controls/Actions/MenuType";
import { convertFormOpenTypeToPurposeType } from "../../../Utilities/SharedUIMethods";
import { ControlParams, IRunWithoutEdit, IStaticControl, IValidateRecommendedControl, IVersioned } from "../../Controls/ControlInterfaces";
import { FormManager } from "../../FormManager/IFormManager";
import { UIManager } from "../../UIManager";
import { IWindowable } from "../../UIManagerInterfaces";
import { IValidationMaster } from "../FormControl";
import { FrameSharedMethods } from "../Frames/FrameSharedMethods";
import { FormLayout, FrameRefreshType } from "../IForm";
import { ModalWindowType } from "../ModalWindow";
import { IValidationOverview } from "../ValidationManager";
import { XGAction } from "./XGAction";

const logger = LazyLoadingSectionLogger.create(LoggingSection.parametricFormAction);

export class ParametricFormAction extends XGAction implements IStaticControl, IDisposable, IEnableInEditControl, IVersioned, IRunWithoutEdit, IValidateRecommendedControl {
    //#region Fields

    /**
    * Atribut třídy volaného formuláře,
    * který se naplní aktuálním objektem volajícího formuláře
    */
    public formName: string;
    public linkAttrForSelObj: string;
    public attributeWithObject: string;
    public masterAttr: string;
    public readonly onFormOpenMethod: string;
    public readonly onFormCloseMethod: string;
    public refreshType: FrameRefreshType;
    public afterGuiCreateFill: string;

    private _formLayout: FormLayout;
    private _purposeType: MenuType;
    private _runWithoutEdit: boolean;
    //definuje jestli se u spuštěného formuláře mají číst záznamy z verzovací tabulky nebo aktuální data
    private _readVersioned: boolean;

    /**
     * Příznak, zda před vykonáním metody musí zkontrolovat doporučené atributy.
     */
    public readonly validateRecommended: boolean;

    /**
     * Příznak, zda před vykonáním metody musí zkontrolovat odloženě povinné atributy.
     */
    public readonly validatePostReq: boolean;
    //#endregion Fields

    //#region Properties

    public override get context(): ActionContext {
        return (this.isStatic || this.useMasterObject) ? ActionContext.frameAction : ActionContext.objectAction;
    }

    public override get actionType(): ActionType {
        return ActionType.parametricForm;
    }

    public override get supportedSelectionModes(): SelectionType {
        return this.isStatic ? SelectionType.all : SelectionType.single;
    }

    /**
     * Pokud není Purpose type nastaven, použije se ToMenu
     */
    public get purposeType(): MenuType | undefined {
        if (this._purposeType === MenuType.mtNone)
            return this.toMenu;
        return this._purposeType;
    }

    public set purposeType(value: MenuType | undefined) {
        if (value !== undefined) {
            switch (value) {
                case MenuType.mtActions:
                case MenuType.mtForm:
                case MenuType.mtConditionForm:
                case MenuType.mtPrint:
                    this._purposeType = value;
                    break;
                default:
                    this._purposeType = MenuType.mtNone;
                    logger.error(`Value '${MenuType[value]}' of PurposeType is invalid.`); // `
            }
        } else
            this._purposeType = MenuType.mtNone;
    }

    /**
     * Vlástnost Rights.RunWithoutEdit se nastavuje dle nastavení v GUI
     */
    public get runWithoutEdit(): boolean {
        //Pokud tato akce otevirá formulář, který nepořebuje aktuální objekt, to může být přestupná i když žadný aktuální objekt není vybran
        if (!this.linkAttrForSelObj)
            return true;
        else
            return this._runWithoutEdit;
    }

    public get readVersioned(): boolean {
        return this._readVersioned;
    }

    public readonly isStatic: boolean | null;
    //#endregion Properties

    //#region Constructors

    constructor(params: ControlParams) {
        super(params);
        this.element.classList.add("ParametricFormAction");
        this.setToMenu(MenuType.mtPrint);
        this.formName = this.element.getAttribute("data-formname");
        this.linkAttrForSelObj = this.element.getAttribute("data-linkattrforselobj");
        this.attributeWithObject = this.element.getAttribute("data-attributewithobject");
        this.masterAttr = this.element.getAttribute("data-masterattr");
        this._runWithoutEdit = this.getBoolDataAttr("runwithoutedit");
        this.onFormOpenMethod = this.element.getAttribute("data-onformopenmethod");
        this.onFormCloseMethod = this.element.getAttribute("data-onformclosemethod");
        this.afterGuiCreateFill = this.element.getAttribute("data-AfterGUICreateFill");

        this.validateRecommended = this.getBoolDataAttr("validaterecommended");
        this.validatePostReq = this.getBoolDataAttr("validatepostreq");
        this._useMasterContext = this.enableInEdit || this.getBoolDataAttr("UseMasterContext", false);
        this.refreshType = FrameSharedMethods.parseFrameRefreshType(this.element.getAttribute("data-refreshactual"), this.getBoolDataAttr("refreshlist"), FrameRefreshType.recordOnChange);
        this._formLayout = FrameSharedMethods.parseFormLayout(this.element.getAttribute("data-formlayout"));

        this.purposeType = EnumUtils.getEnumValueFromName(MenuType, this.element.getAttribute("data-purposetype"));
        this._readVersioned = this.getBoolDataAttr("readversioned");
        this.isStatic = !this.linkAttrForSelObj && !this.useMasterObject;
    }
    //#endregion Constructors

    //#region Methods

    /**
    * Vstupní bod pro spuštění provedení metody
    */
    protected override async _internalExecute(context?: IActionContext): Promise<void> {
        const { actualObject, ownerFrame } = this.executionContext;
        if (!ownerFrame && !actualObject) {
            throw new InvalidOperationException("Executing ParametricFormAction without owner frame is forbidden");
        }

        const detailFrame = this.ownerForm?.detailFrame;
        let validationResult: IValidationOverview | undefined;

        // Pokud má nastavený atribut validateRecommended jak detail, tak akce, validace recommended se spustí jenom jednou, a to přednostně v detailu.
        // Pokud má nastavený atribut validateRecommended jen akce a detail ne, nejprve se zvaliduje akce a její výsledky (chyby) se uloží do validationResult.
        // Následně proběhne kontrola a uložení formuláře a chyby z validace jsou předány do _checkPostRightsActions k dalšímu postprocessingu.
        // U validace postponed dochází jak u akce, tak na detailu.
        if (detailFrame instanceof IValidateRecommendedControl && ownerFrame instanceof IValidationMaster) {
            if (!detailFrame?.validateRecommended || this.validatePostReq) {
                //Je potřebna kontrola vyplnění odloženě povinných a/nebo doporučených polí
                const ownerValidated = await this._validateOwnerRightsActions(this.validateRecommended, this.validatePostReq);

                const validationManager = ownerFrame.validationManager;
                validationResult = {
                    hasErrors: validationManager?.hasErrors,
                    ownerValidated,
                    validationMessages: validationManager?.getErrorMessages(),
                };

                // V rámci tohoto bloku dochází k validaci práv recommended a postponed u akce.
                // Výsledky z validace se používají i dále v kódu, ale je třeba validation managera pročistit od dosažených validačních chyb akce.
                // Je to potřeba kvůli tomu, že při volání _checkAndSaveForm níže si detail dělá ještě kontrolu a validaci zvlášť a mohly by se duplikovat validační chyby.
                validationManager.clearErrors(null, ValidationErrorType.recommendedError);
                validationManager.clearErrors(null, ValidationErrorType.postponedError);
            }
        }

        // Zkontroluje se uložení změn na owneru a jeho nadřízených framech. Validaci si detail udělá sám na základě jeho atributů.
        if (!(await this._checkAndSaveForm({ checkPostponed: this.validatePostReq, cancelEditIfNotEdited: true }))) {
            return;
        }

        // Pokud nemám označen záznam a je vyplněn LinkAttrForSelObj, tak nemůžu spustit akci.
        // K chybě docházelo při YNC dialogu nad novým záznamem a kliknutí na naukládat.
        if (!context && actualObject == null && this.linkAttrForSelObj != null) {
            return;
        }

        if (this.onFormOpenMethod && actualObject != null) {
            try {
                await actualObject.run({
                    methodName: this.onFormOpenMethod,
                    requestContext: this._getRequestContext(),
                });
            } catch (err) {
                ErrorHandler.instance.handleError(err);
                return;
            }
        }

        // Pokud je u akce nastaven atribut validateRecommended a zároveň není nastaven v detailu, provede se processing v _checkPostRightsActions.
        // Ten zprocesuje výsledky validace a případně zobrazí uživatelský dialog vyžadující akci od uživatele. Poté je akce vykonána.
        // Pokud není u akce nastaven atribut validateRecommended, vykoná se akce zrovna bez nějaké předchozí kontroly a interakce.
        if (detailFrame instanceof IValidateRecommendedControl && (!detailFrame?.validateRecommended || this.validatePostReq)) {
            if (await this._checkPostRightsActions(this.validateRecommended, this.validatePostReq, validationResult)) {
                await this._confirmedExecute(context);
            }
        } else {
            await this._confirmedExecute(context);
        }
    }

    protected async _confirmedExecute(context?: IActionContext): Promise<void> {
        const callingObject = context ? await OWManager.getObject(context.ids[0] ?? "", context.className) : this.executionContext.actualObject; //uloží si referenci na objekt, nad kterým byla akce spuštěna; instance zůstane zachována i po případných refreších na volajícím formuláři
        const { ownerFrame } = this.executionContext;

        const showModal = async (): Promise<IWindowable> => {
            let masterObject = callingObject;

            const formInfo = await FrameSharedMethods.getDefaultFormName(this.formName, this.ownerForm, callingObject, (ownerFrame || this.owner).frameContext);
            let purposeType: MenuType;
            if (formInfo.PurposeType != null) {
                purposeType = convertFormOpenTypeToPurposeType(formInfo.PurposeType);
            } else {
                purposeType = this.purposeType;
            }

            const masterAttr = formInfo.MasterAttr || this.masterAttr;

            if (masterAttr != null && this.linkAttrForSelObj != null) {
                await OWManager.publishInstance(callingObject.instanceID, masterAttr, this._getRequestContext());
                masterObject = await callingObject.getValue(masterAttr) as OWObject;
            }
            const ownerForm = this.ownerForm ? this.ownerForm : (this.owner ? this.owner.ownerForm : undefined);

            //pokud se otevírá formulář z formuláře, který obsahuje verzovaná data tak i podřízený formulář bude číst verzovaná data, i když není verzování nastaveno na DetailFormAction
            const localReadVersioned = (this.readVersioned != null && this.readVersioned) || ownerForm && ownerForm.readVersioned;

            if (ownerFrame && this._useMasterContext) {
                await ownerFrame.createContext();
                ownerFrame.isContextCreatedByAction = true;
            }

            return await FormManager.instance.openFormModal({
                formName: formInfo.FormName,
                parametricData: {
                    masterObject: masterObject,
                    masterLinkToAttr: formInfo.DetailAssocName || this.linkAttrForSelObj,
                    openInEdit: true,
                    formContext: ownerFrame?.frameContext,
                    ownerForm: ownerForm,
                    executerFrame: ownerFrame,
                    frameToRefreshAfterSetFormActive: (this.refreshType !== FrameRefreshType.none) ? (ownerFrame || this.owner) : null,
                    frameRefreshType: this.refreshType,
                    purposeType: purposeType,
                    readVersioned: localReadVersioned,
                    isImplicit: FrameSharedMethods.isFormNameImplicit(this.formName),
                    transactionContextGetter: ownerFrame?.frameContext?.getTransactionContextId,
                    formLayout: this._formLayout,
                    setValues: this.afterGuiCreateFill,
                },
                onClose: async () => {
                    if (this.onFormCloseMethod != null) {
                        try {
                            UIManager.instance.showLoader();
                            await callingObject.run({
                                methodName: this.onFormCloseMethod,
                                requestContext: this._getRequestContext(),
                            });
                        } finally {
                            UIManager.instance.hideLoader();
                        }
                    }
                },
                type: ModalWindowType.parametricForm,
            });
        };
        try {
            UIManager.instance.showLoader();

            await showModal();
        } catch (e) {
            logger.warn(e);
        } finally {
            UIManager.instance.hideLoader();
        }
    }
    //#endregion Methods
}
